import React, { useState, useEffect } from 'react';
import { Search, Filter, Bell, Clock, User, MapPin, Phone, Calendar, Package2, Eye, X, ChevronDown, Loader2, CheckCircle2, Circle, Truck, ShoppingBag, PackageCheck } from 'lucide-react';
import Pagination from './Pagination';
import { fetchOrders as apiFetchOrders, updateOrderStatus as apiUpdateOrderStatus, updatePaymentStatus as apiUpdatePaymentStatus } from '../api';
import { getProductImageUrl } from '../api/productsApi';

// --- Tracking Steps Config ---
const TRACKING_STEPS = [
  { key: 'pending',    label: 'Order Placed',  icon: ShoppingBag },
  { key: 'processing', label: 'Processing',    icon: Package2 },
  { key: 'shipped',    label: 'Shipped',       icon: Truck },
  { key: 'completed',  label: 'Delivered',     icon: PackageCheck },
];

const getStepIndex = (status) => {
  if (status === 'cancelled') return -1;
  return TRACKING_STEPS.findIndex(s => s.key === status);
};

const OrderDetailsModal = ({ order, onClose, onStatusChange }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  if (!order) return null;

  const handleStatusChange = async (newStatus) => {
    if (newStatus === order.status) return;
    setIsUpdating(true);
    await onStatusChange(order.id, newStatus);
    setIsUpdating(false);
  };

  const currentStepIdx = getStepIndex(order.status);
  const isCancelled = order.status === 'cancelled';

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-slate-100/80 animate-fade-in flex flex-col justify-between">
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4.5 flex justify-between items-center rounded-t-[28px] z-10">
          <div>
            <h3 className="text-lg font-bold text-slate-800 tracking-tight">{order.id}</h3>
            <p className="text-xs font-semibold text-slate-400 mt-1">Placed on {order.date} • {order.time}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          {/* Status Row */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Order Status Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Order Status:</span>
              <div className="relative">
                <select
                  value={order.status}
                  disabled={isUpdating || isCancelled}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className={`appearance-none pl-3 pr-8 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 focus:outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                    isCancelled ? 'bg-rose-50 text-rose-700 focus:ring-rose-200' :
                    order.status === 'completed' ? 'bg-emerald-50 text-emerald-700 focus:ring-emerald-200' :
                    order.status === 'processing' ? 'bg-blue-50 text-blue-700 focus:ring-blue-200' :
                    order.status === 'shipped' ? 'bg-purple-50 text-purple-700 focus:ring-purple-200' :
                    'bg-amber-50 text-amber-700 focus:ring-amber-200'
                  }`}
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <ChevronDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-current opacity-60" />
                {isUpdating && <span className="absolute -right-6 top-1/2 -translate-y-1/2"><Loader2 size={12} className="animate-spin text-slate-400" /></span>}
              </div>
            </div>

            {/* Payment badge */}
            <span className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
              order.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700' :
              order.paymentStatus === 'failed' ? 'bg-rose-50 text-rose-700' :
              'bg-amber-50 text-amber-700'
            }`}>
              Payment: {order.paymentStatus || 'pending'}
            </span>
            {order.paymentMode && (
              <span className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
                {order.paymentMode}
              </span>
            )}
          </div>

          {/* Order Tracking Timeline */}
          {!isCancelled ? (
            <div className="bg-slate-50 border border-slate-100/60 rounded-2xl p-5">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4">Order Tracking</p>
              <div className="flex items-start justify-between relative">
                {/* connector line */}
                <div className="absolute top-5 left-[calc(12.5%-0px)] right-[calc(12.5%-0px)] h-0.5 bg-slate-200 z-0">
                  <div
                    className="h-full bg-emerald-500 transition-all duration-700"
                    style={{ width: currentStepIdx < 0 ? '0%' : `${(currentStepIdx / (TRACKING_STEPS.length - 1)) * 100}%` }}
                  />
                </div>
                {TRACKING_STEPS.map((step, idx) => {
                  const isDone = currentStepIdx >= idx;
                  const isCurrent = currentStepIdx === idx;
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className="flex flex-col items-center gap-2 z-10 w-1/4">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                        isDone ? 'bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-500/10' :
                        isCurrent ? 'bg-white border-emerald-500 text-emerald-500' :
                        'bg-white border-slate-200 text-slate-300'
                      }`}>
                        {isDone && !isCurrent ? <CheckCircle2 size={16} /> : <Icon size={14} />}
                      </div>
                      <div className="text-center">
                        <p className={`text-[11px] font-bold ${isDone ? 'text-slate-800' : 'text-slate-400'}`}>{step.label}</p>
                        <p className={`text-[9px] font-extrabold uppercase mt-0.5 tracking-wider ${isCurrent ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {idx < currentStepIdx ? 'Done' : idx === currentStepIdx ? 'Current' : 'Next'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-rose-50 border border-rose-100/60 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 shrink-0 shadow-sm border border-rose-200/20">
                <X size={16} />
              </div>
              <div>
                <p className="font-extrabold text-rose-700 text-sm">Order Cancelled</p>
                <p className="text-xs text-rose-500 font-semibold mt-0.5">This order has been cancelled and deactivated.</p>
              </div>
            </div>
          )}

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 border border-slate-100/60 rounded-2xl p-4">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Customer Details</p>
              <p className="font-bold text-slate-800 text-sm">{order.customer}</p>
              {order.phone && <p className="text-xs text-slate-500 font-medium mt-1">{order.phone}</p>}
            </div>
            {order.address && (
              <div className="bg-slate-50 border border-slate-100/60 rounded-2xl p-4">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Delivery Address</p>
                <p className="text-xs text-slate-600 font-medium leading-relaxed">{order.address}</p>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Order Items ({order.items} items)</p>
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 ui-scrollbar">
              {order.products && order.products.length > 0 ? (
                order.products.map((product, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-slate-50 hover:bg-slate-100/40 border border-slate-100 p-3.5 rounded-2xl transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-xl overflow-hidden flex items-center justify-center shrink-0 border border-slate-200/50 shadow-sm">
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <Package2 size={16} className="text-slate-300" />
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-xs line-clamp-1">{product.name}</p>
                        <p className="text-[10px] text-slate-400 mt-1 font-bold">
                          {product.weight && product.weight !== '-' ? `${product.weight} ${product.unit}` : ''} • Qty: {product.quantity || 1}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-extrabold text-slate-800 text-xs">₹{product.totalAmount > 0 ? product.totalAmount : product.price}</p>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5">₹{product.price} × {product.quantity || 1}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-400 italic text-xs bg-slate-50 p-4 rounded-2xl text-center border border-dashed border-slate-200">No product details available</p>
              )}
            </div>
          </div>

          {/* Total */}
          <div className="border-t border-slate-100 pt-4 flex justify-between items-center">
            <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Total Amount</span>
            <span className="text-2xl font-extrabold text-slate-800 tracking-tight">₹{order.total}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2">
          <button
            onClick={onClose}
            className="w-full bg-slate-100 text-slate-700 py-3 rounded-xl hover:bg-slate-200 font-bold tracking-wider text-xs uppercase transition-all duration-200 cursor-pointer shadow-sm"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
};


const Orders = ({ onUpdateStatus, products }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch orders when component mounts
  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const rawOrders = await apiFetchOrders();
      const formatted = rawOrders.map(o => {
        const dateObj = new Date(o.order_date || o.created_at);
        return {
          id: `ORD${String(o.id).padStart(3, '0')}`,
          rawId: o.id,
          customer: o.customer_name || 'Guest User',
          items: o.items ? o.items.length : 0,
          total: parseFloat(o.total_amount) || 0,
          status: o.status || 'pending',
          date: dateObj.toLocaleDateString('en-GB').replace(/\//g, '.'),
          time: dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
          paymentStatus: o.payment_status || 'pending',
          paymentMode: o.payment_mode,
          address: o.delivery_address,
          phone: o.phone || o.customer_phone,
          products: o.items ? o.items.map(item => ({
            name: item.item_name || 'Unknown Item',
            weight: item.product_weight || '-',
            unit: item.product_unit || '',
            price: parseFloat(item.item_price) || 0, // unit price
            totalAmount: parseFloat(item.total_amount) || 0,
            quantity: item.quantity || 1,
            imageUrl: item.product_image || item.combo_image ? getProductImageUrl(item.product_image || item.combo_image) : null
          })) : []
        };
      });
      setOrders(formatted);
    } catch (error) {
      console.error('Failed to load orders', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    // orderId here is the string ID "ORD001", so fetch the object to find rawId
    const orderObj = orders.find(o => o.id === orderId);
    if (!orderObj?.rawId) return;

    try {
      await apiUpdateOrderStatus(orderObj.rawId, newStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
    } catch (err) {
      console.error('Failed to update order status', err);
      alert('Failed to update status. Please try again.');
    }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [lastOrderCount, setLastOrderCount] = useState(orders.length);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (orders.length > lastOrderCount) {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
      audio.play().catch(() => {});
      
      if (Notification.permission === 'granted') {
        new Notification('New Order Received!', {
          body: `Order #${orders[orders.length - 1]?.id} from ${orders[orders.length - 1]?.customer}`,
          icon: '/favicon.ico'
        });
      }
    }
    setLastOrderCount(orders.length);
  }, [orders.length, lastOrderCount]);

  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.customer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.date.split('.').reverse().join('-'));
      const today = new Date();
      
      if (dateFilter === 'custom' && customStartDate && customEndDate) {
        const startDate = new Date(customStartDate);
        const endDate = new Date(customEndDate);
        matchesDate = orderDate >= startDate && orderDate <= endDate;
      } else {
        const daysDiff = Math.floor((today - orderDate) / (1000 * 60 * 60 * 24));
        if (dateFilter === 'today') matchesDate = daysDiff === 0;
        else if (dateFilter === 'week') matchesDate = daysDiff <= 7;
        else if (dateFilter === 'month') matchesDate = daysDiff <= 30;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Sort orders by date and time (descending)
  const sortedOrders = filteredOrders.sort((a, b) => {
    const dateA = new Date(`${a.date} ${a.time || '00:00'}`);
    const dateB = new Date(`${b.date} ${b.time || '00:00'}`);
    return dateB - dateA;
  });

  // Pagination
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = sortedOrders.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight">
              Orders Management
            </h1>
            <p className="text-slate-500 mt-1.5 font-medium">Track and process customer organic product purchases.</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="bg-blue-50/60 border border-blue-100/60 px-5 py-2.5 rounded-2xl shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-blue-500 text-white shadow-sm shadow-blue-500/10">
                  <Bell size={14} />
                </div>
                <span className="text-blue-800 text-xs font-bold uppercase tracking-wider">{orders.filter(o => o.status === 'pending').length} Pending</span>
              </div>
            </div>
            <div className="bg-emerald-50/60 border border-emerald-100/60 px-5 py-2.5 rounded-2xl shadow-sm">
              <span className="text-emerald-800 text-xs font-bold uppercase tracking-wider">Total: {orders.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Options */}
      <div className="ui-card ui-card-pad mb-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200/80 rounded-xl px-4 py-2">
            <Search className="text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search orders by Order ID or Customer Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 outline-none text-slate-700 bg-transparent font-medium text-sm"
            />
          </div>
          
          <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="ui-input py-2 font-semibold border-slate-200 text-xs uppercase"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="return">Return</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date Period:</span>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="ui-input py-2 font-semibold border-slate-200 text-xs uppercase"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/60 rounded-xl px-3 py-1.5 animate-fade-in">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="bg-transparent border-0 outline-none text-xs font-bold text-slate-700"
                />
                <span className="text-slate-400 font-extrabold text-xs">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="bg-transparent border-0 outline-none text-xs font-bold text-slate-700"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Orders View */}
      <div>
        {isLoading ? (
          <div className="ui-card p-16 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mb-4 text-emerald-500" size={36} />
            <p className="font-semibold text-xs uppercase tracking-wider">Loading orders data...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
              {paginatedOrders.map(order => {
                const isCompleted = order.status === 'completed' || order.status === 'Delivered';
                const isProcessing = order.status === 'processing' || order.status === 'Shipped';
                return (
                  <div key={order.id} className="ui-card bg-gradient-to-br from-white to-slate-50/20 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-slate-100 flex flex-col justify-between group">
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-base font-extrabold text-slate-800 tracking-tight group-hover:text-emerald-600 transition-colors">#{order.id}</h3>
                          <div className="flex items-center gap-1.5 mt-1 font-semibold text-xs text-slate-400">
                            <Clock size={12} className="text-slate-400 shrink-0" />
                            <span>{order.date} • {order.time || '10:30 AM'}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <div className="relative">
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                              className={`appearance-none pl-3 pr-7 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-sm border-0 cursor-pointer focus:outline-none ${
                                order.status === 'completed' ? 'bg-emerald-50 text-emerald-700' :
                                order.status === 'processing' ? 'bg-blue-50 text-blue-700' :
                                order.status === 'shipped' ? 'bg-purple-50 text-purple-700' :
                                order.status === 'cancelled' ? 'bg-rose-50 text-rose-700' :
                                order.status === 'return' ? 'bg-orange-50 text-orange-700' :
                                'bg-amber-50 text-amber-700'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                              <option value="return">Return</option>
                            </select>
                            <ChevronDown className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" size={10} />
                          </div>
                          <span className={`inline-block px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider ${
                            order.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200/20' :
                            order.paymentStatus === 'pending' ? 'bg-amber-100 text-amber-800 border border-amber-200/20' :
                            'bg-rose-100 text-rose-800 border border-rose-200/20'
                          }`}>
                            {order.paymentStatus || 'Pending'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center shadow-sm text-slate-600 shrink-0 border border-slate-200/50">
                            <User size={15} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-slate-800 text-xs truncate">{order.customer}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5 font-bold uppercase tracking-wider">{order.items} items ordered</p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-slate-100 pt-4 flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total</span>
                        <span className="text-lg font-extrabold text-slate-800">₹{order.total}</span>
                      </div>
                      
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="w-full bg-slate-50 border border-slate-200 text-slate-700 py-2.5 px-4 rounded-xl hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-100/60 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer transition-all duration-200 shadow-sm"
                      >
                        <Eye size={14} />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                );
              })}
              {paginatedOrders.length === 0 && (
                <div className="col-span-1 lg:col-span-2 xl:col-span-3 text-center py-12 text-slate-400 font-semibold border border-slate-200/40 rounded-3xl bg-white">
                  No orders found matching your filters.
                </div>
              )}
            </div>
            
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={sortedOrders.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
      
      {selectedOrder && (
        <OrderDetailsModal 
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusChange={async (orderId, newStatus) => {
            await updateOrderStatus(orderId, newStatus);
            // Update the selectedOrder state so the modal reflects the change immediately
            setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : prev);
          }}
        />
      )}
    </div>
  );
};

export default Orders;